// "A note on this record changed" — a one-line pub/sub between the
// notes dock and any page still rendering that record's notes.
//
// Why this exists (S13): the dock mounts in PageLayout's chrome, while
// the Household and Contact pages render their own notes list from the
// detail payload they already fetched. Once the composer moved into the
// dock, those two facts combined into a real bug — write a note, and the
// Notes tab you're looking at wouldn't show it until a reload.
//
// A DOM CustomEvent rather than context or a store, deliberately: the
// dock and those pages have no common ancestor below PageLayout, the
// payload is two strings, and the pages already own a refetch function
// (the one that used to be wired as `onNoteAdded`). This reconnects
// those two ends without threading a prop through the whole shell.
//
// The matching RULE lives in notes-changed-match.ts so it can be tested
// without a DOM — see that file for why.

import { notesChangeMatches, type NotesChangedDetail } from "./notes-changed-match";

export type { NotesChangedDetail };

const EVENT_NAME = "stewardship:notes-changed";

export function emitNotesChanged(detail: NotesChangedDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<NotesChangedDetail>(EVENT_NAME, { detail }));
}

/**
 * Call `onChanged` whenever a note changes on this exact record. Returns
 * the unsubscribe function, so it drops straight into a useEffect.
 */
export function subscribeNotesChanged(
  match: NotesChangedDetail,
  onChanged: () => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (e: Event) => {
    if (notesChangeMatches(match, (e as CustomEvent<NotesChangedDetail>).detail)) onChanged();
  };
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}
